/**
 * 用量管理API
 */
import type { AxiosRequestConfig } from 'axios'
import { request } from '@/shared/api/request'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

// 类型定义
export interface UsageQuota {
  id: number
  group_id: number
  usage_type: 'character' | 'time'
  total_quota: number
  used_quota: number
  remaining_quota: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface UsageRecord {
  id: number
  user_id: number
  user_name: string
  group_id: number
  service_type: string
  usage_type: 'character' | 'time'
  amount: number
  cost: number
  api_name?: string
  project_id?: number
  project_type?: string
  created_at: string
}

export interface UsageQuotaResponse {
  data: UsageQuota[]
  total: number
  skip: number
  limit: number
}

export interface UsageRecordListResponse {
  items: UsageRecord[]
  total: number
  offset: number
  limit: number
  has_next: boolean
  has_prev: boolean
}

export interface UsageCheckRequest {
  user_id: number
  usage_type: 'character' | 'time'
  amount: number
}

export interface UsageCheckResponse {
  success: boolean
  message: string
  can_proceed: boolean
  quota_info?: {
    total: number
    used: number
    remaining: number
  }
}

export interface UsageConsumeRequest {
  user_id: number
  service_type: string
  usage_type: 'character' | 'time'
  amount: number
  api_name?: string
  project_id?: number
  project_type?: string
}

export interface UsageConsumeResponse {
  success: boolean
  message: string
  usage_record_id?: number
  remaining_quota?: number
}

export interface QuotaAllocation {
  group_id: number
  character_quota?: number
  time_quota?: number
}

export interface BatchQuotaAllocation {
  allocations: QuotaAllocation[]
}

export interface QuotaResetRequest {
  group_id: number
  usage_type: 'character' | 'time'
  new_total: number
}

export interface UsageStatistics {
  total_character_usage: number
  total_time_usage: number
  service_breakdown: Record<string, {
    character_usage: number
    time_usage: number
  }>
  daily_usage?: Array<{
    date: string
    character_usage: number
    time_usage: number
  }>
  top_users?: Array<{
    user_id: number
    user_name: string
    character_usage: number
    time_usage: number
  }>
  period_start?: string
  period_end?: string
}

export interface UsageConsumptionReport {
  group_id: number
  period_days: number
  quota_summary: Record<string, {
    total: number
    used: number
    remaining: number
    usage_rate: number
  }>
  daily_usage: Record<string, {
    character: number
    time: number
  }>
  total_records: number
}

export interface LowQuotaAlert {
  group_id: number
  group_name: string
  usage_type: string
  total_quota: number
  used_quota: number
  remaining_quota: number
  usage_rate: number
}

export interface ManagementSummary {
  total_groups: number
  active_quotas: number
  low_quota_alerts: LowQuotaAlert[]
  recent_usage: UsageRecord[]
  usage_alerts?: Array<{
    user_id: number
    group_id: number
    group_name: string
    usage_type: 'character' | 'time'
    remaining_quota: number
    usage_rate: number
  }>
  negative_usage_users?: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_type: 'character' | 'time'
  }>
  character_alerts?: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_rate: number
  }>
  time_alerts?: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_rate: number
  }>
}

export interface UsageSummary {
  total_groups: number
  active_quotas: number
  usage_alerts: Array<{
    user_id: number
    group_id: number
    group_name: string
    usage_type: 'character' | 'time'
    remaining_quota: number
    usage_rate: number
  }>
  character_alerts: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_rate: number
  }>
  time_alerts: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_rate: number
  }>
  negative_usage_users: Array<{
    user_id: number
    group_id: number
    group_name: string
    remaining_quota: number
    usage_type: 'character' | 'time'
  }>
  low_quota_alerts: Array<{
    group_id: number
    group_name: string
    usage_type: 'character' | 'time'
    usage_rate: number
  }>
}

export interface MyUsageSummary {
  user_id: number
  user_name: string
  group_id: number
  quotas: {
    character: {
      total: number
      used: number
      remaining: number
      usage_rate: number
    }
    time: {
      total: number
      used: number
      remaining: number
      usage_rate: number
    }
  }
  recent_usage: {
    character: number
    time: number
    records_count: number
  }
  last_updated: string
}

/**
 * 获取用量配额列表
 */
export const getUsageQuotas = (
  params?: {
  skip?: number
  limit?: number
  group_id?: number
  usage_type?: 'character' | 'time'
  is_active?: boolean
},
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UsageQuotaResponse> => {
  return request.get('/usage/quotas', { params, ...config })
}

/**
 * 获取所有配额（兼容旧代码）
 */
export const getAllQuotas = getUsageQuotas

/**
 * 获取用量记录列表
 */
export const getUsageRecords = (
  params: {
  offset?: number
  limit?: number
  user_id?: number
  group_id?: number
  service_type?: string
  usage_type?: 'character' | 'time'
  start_date?: string
  end_date?: string
},
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UsageRecordListResponse> => {
  return request.get('/usage/records', { params, ...config })
}

/**
 * 检查用量是否足够
 */
export const checkUsage = (data: UsageCheckRequest): Promise<UsageCheckResponse> => {
  return request.post('/usage/check', data)
}

/**
 * 消费用量
 */
export const consumeUsage = (data: UsageConsumeRequest): Promise<UsageConsumeResponse> => {
  return request.post('/usage/consume', data)
}

/**
 * 分配配额
 */
export const allocateQuota = (data: QuotaAllocation): Promise<UsageQuota> => {
  return request.post('/usage/quotas/allocate', data)
}

/**
 * 批量分配配额
 */
export const batchAllocateQuota = (data: BatchQuotaAllocation): Promise<UsageQuota[]> => {
  return request.post('/usage/quotas/batch-allocate', data)
}

/**
 * 重置配额
 */
export const resetQuota = (data: QuotaResetRequest): Promise<UsageQuota | { message: string }> => {
  return request.put('/usage/quotas/reset', data)
}

/**
 * 获取用户组配额
 */
export const getGroupQuotas = (groupId: number, usageType?: 'character' | 'time'): Promise<UsageQuota[]> => {
  const params = usageType ? { usage_type: usageType } : {}
  return request.get(`/usage/quotas/group/${groupId}`, { params })
}

/**
 * 创建或更新配额
 */
export const createOrUpdateQuota = (data: {
  group_id: number
  usage_type: 'character' | 'time'
  total_quota: number
}): Promise<UsageQuota> => {
  return request.post('/usage/quotas', data)
}

/**
 * 分配配额（兼容旧代码）
 */
export const allocateQuotas = (data: QuotaAllocation): Promise<UsageQuota[]> => {
  return request.post('/usage/quotas/allocate', data)
}

/**
 * 切换配额状态
 */
export const toggleQuotaStatus = (quotaId: number): Promise<{ message: string }> => {
  return request.patch(`/usage/quotas/${quotaId}/toggle-status`)
}

/**
 * 获取消费报告
 */
export const getConsumptionReport = (groupId: number, days: number = 30): Promise<UsageConsumptionReport> => {
  return request.get(`/usage/reports/consumption/${groupId}`, {
    params: { days }
  })
}

/**
 * 获取配额不足警告
 */
export const getLowQuotaAlerts = (threshold: number = 10.0): Promise<LowQuotaAlert[]> => {
  return request.get('/usage/alerts/low-quota', {
    params: { threshold }
  })
}

/**
 * 获取管理摘要
 */
export const getManagementSummary = (
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<ManagementSummary> => {
  return request.get('/usage/management/summary', config)
}

/**
 * 获取用量统计
 */
export const getUsageStatistics = (params?: {
  start_date?: string
  end_date?: string
  group_id?: number
  user_id?: number
}): Promise<UsageStatistics> => {
  return request.get('/usage/statistics', { params })
}

/**
 * 获取用量概览
 */
export const getUsageSummary = (): Promise<UsageSummary> => {
  return request.get('/usage/summary')
}

/**
 * 获取我的用量概览
 */
export const getMyUsageSummary = (): Promise<MyUsageSummary> => {
  return request.get('/usage/my-summary')
}

/**
 * 获取用户用量记录
 */
export const getUserUsageRecords = (userId: number, params?: {
  offset?: number
  limit?: number
  start_date?: string
  end_date?: string
}): Promise<UsageRecordListResponse> => {
  return request.get(`/usage/users/${userId}/records`, { params })
}

/**
 * 获取组用量记录
 */
export const getGroupUsageRecords = (groupId: number, params?: {
  offset?: number
  limit?: number
  start_date?: string
  end_date?: string
}): Promise<UsageRecordListResponse> => {
  return request.get(`/usage/groups/${groupId}/records`, { params })
}

// 导出API对象（兼容旧代码）
export const usageApi = {
  // 基础API
  getUsageQuotas,
  getAllQuotas,
  getUsageRecords,
  checkUsage,
  checkUsageQuota: checkUsage,
  consumeUsage,
  allocateQuota,
  allocateQuotas,
  batchAllocateQuota,
  batchAllocateQuotas: batchAllocateQuota,
  resetQuota,
  getUsageStatistics,
  getUsageSummary,
  getMyUsageSummary,
  getUserUsageRecords,
  getGroupUsageRecords,
  // 扩展API
  getGroupQuotas,
  createOrUpdateQuota,
  toggleQuotaStatus,
  getConsumptionReport,
  getLowQuotaAlerts,
  getManagementSummary,
  // 用户相关API
  getMyQuota: (params?: { user_id?: number }, config?: AxiosRequestConfig | BatchRequestConfig) =>
    request.get<UsageQuota[]>('/usage/my-quota', { params, ...config }),
  getMyUsageRecords: (params?: {
    user_id?: number
    service_type?: string
    usage_type?: 'character' | 'time'
    days?: number
    start_date?: string
    end_date?: string
    limit?: number
    offset?: number
  }, config?: AxiosRequestConfig | BatchRequestConfig) =>
    request.get<UsageRecordListResponse>('/usage/my-records', { params, ...config }),
  getMyUsageStatistics: (days?: number) => request.get<UsageStatistics>('/usage/my-statistics', { 
    params: days ? { days } : {} 
  }),
  getUserQuota: () => request.get<{
    quotas: Array<{
      usage_type: string
      total_quota: number
      used_quota: number
      remaining_quota: number
      is_active: boolean
    }>
  }>('/usage/user-quota'),
  checkQuota: (data: {
    usage_type: string
    amount: number
  }) => request.post<{
    can_proceed: boolean
    message: string
    quota_info?: {
      total: number
      used: number
      remaining: number
    }
  }>('/usage/check-quota', data)
}

export default usageApi

